import { useMutation, useQueryClient } from "@tanstack/react-query";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import LinkedShowsPanel from "document/linkshow/LinkedShowsPanel";
import { EsignDraftSection } from "eSign/components/DocumentDetails/EsignDraftSection";
import DraftViewerPanel from "eSign/panelsegment/ViewerPanelSegment";
import ProductsPanel, { getProductsByParentQueryKey } from "eSign/panelsegment/ProductsPanel";
import React, { useState } from "react";
import TwoColumnLayout from "../../layout/TwoColumnLayout";
import ESignActivityPanel from "../components/activity/ESignActivityPanel";
import ChangeLifecycleButtons from "../components/ChangeLifecycleButtons";
import ESignOptionsPanel from "../components/optionspanel/ESignOptionsPanel";
import ViewDocumentButton from "../components/ViewDocumentButton";
import FilesPanel from "../panelsegment/FilesPanel";
import { SenderPanel } from "eSign/panelsegment/SenderPanel";
import { SignerPanel } from "eSign/panelsegment/SignerPanel";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { ContactParticipant } from "components/Picker/ContactParticipantPicker/ContactParticipantModalPicker";
import NotesPanel from "note/list/app";
import { invalidateContactCache } from "utilities/api/contactQuery";
import { useModals } from "Modals/ModalsProvider";
import { DocumentReminderApp } from "DocumentReminder/AppForDocuments";
import { DocumentTypeWithoutUnknown } from "utilities/documentUtils";
export interface EsignDocumentInfo {
  bookkeeping: server.dto.BookkeepingSmall
  currency: server.dto.Currencies
  exchangeRate: number

  brand?: server.dto.BrandSmall
  dueInDays: number
  owner: server.dto.UserSmall
  allowAutoSign: boolean
}

interface Props {
  viewModel: server.viewmodel.ESignDetailFrontModel
}

const DraftPanel = ({ viewModel }: Props) => {
  const [bookkeeping, setBookkeeping] = useState<server.dto.BookkeepingSmall>(viewModel.details.bookkeeping);
  const [filesNeedingSigning, setFilesNeedingSigning] = useState(viewModel.filesThatNeedToBeSigned);
  const [otherFiles, setOtherFiles] = useState(viewModel.otherFiles);
  const [sender, setSender] = useState(viewModel.sender);
  const [signer, setSigner] = useState(viewModel.signer);

  const [noSenderError, setNoSenderError] = useState(false);
  const [noSignerError, setNoSignerError] = useState(false);
  const [fileError, setFileError] = useState(false);

  const [esignDocumentFormState, setEsignDocumentFormState] = React.useState<EsignDocumentInfo>({
    bookkeeping: viewModel.details.bookkeeping,
    currency: viewModel.details.currency.value,
    exchangeRate: viewModel.details.exchangeRate,

    brand: viewModel.details.brand,
    dueInDays: viewModel.details.dueInDays,
    owner: viewModel.details.owner,
    allowAutoSign: viewModel.details.allowAutoSign,
  });

  const productsByParentQueryKey = getProductsByParentQueryKey(viewModel.details.documentType, viewModel.details.documentId);

  const deleteSenderMutation = useMutation(window.CoreApi.ESign.DeleteParticipant);
  const deleteSignerMutation = useMutation(window.CoreApi.ESign.DeleteParticipant);

  const getDocumentId = () => viewModel.details.documentId;

  const isDraftValid = () => {
    const hasSender = sender != null;
    const hasSigner = signer != null;
    const hasFilesThatNeedSigning = filesNeedingSigning.length > 0;

    return {
      hasErrors: !hasSender || !hasSigner || !hasFilesThatNeedSigning,
      hasSender,
      hasSigner,
      hasFilesThatNeedSigning,
    };
  };

  const onReviewClickValidate = () => {
    const { hasSender, hasSigner, hasFilesThatNeedSigning } = isDraftValid();

    if (!hasSender || !hasSigner || !hasFilesThatNeedSigning) {
      if (!hasSender || !hasSigner) {
        window.soneTools.scrollTo("#senderPanel");
      } else if (!hasFilesThatNeedSigning) {
        window.soneTools.scrollTo("#filesThatNeedSigningPanel");
      }
      setNoSenderError(!hasSender);
      setNoSignerError(!hasSigner);
      setFileError(!hasFilesThatNeedSigning);

      return false;
    }

    return true;
  };

  const queryClient = useQueryClient();

  const onLinkedShowsSaved = async () => {
    queryClient.invalidateQueries({ queryKey: productsByParentQueryKey });
  };

  const onBookkeepingOrCurrencyChange = (newBookkeeping: server.dto.BookkeepingSmall) => {
    queryClient.invalidateQueries(productsByParentQueryKey);
    setBookkeeping(newBookkeeping);
  }

  const validateOnFromTemplateClick = () => {
    const { hasSender, hasSigner } = isDraftValid();

    if (!hasSender || !hasSigner) {
      if (!hasSender || !hasSigner) {
        window.soneTools.scrollTo("#senderPanel");
      }

      setNoSignerError(!hasSigner);
      setNoSenderError(!hasSender);
      return false;
    }

    return true;
  };

  const onRemoveSender = async () => {
    if (deleteSenderMutation.isLoading) return;

    const res = await deleteSenderMutation.mutateAsync(sender.eSignParticipantId);

    if (!res.data.hasErrors) {
      setSender(null);
    } else {
      window.displayGenericModal("Error", res.data.errors[0].message, "tiny", "red");
    }
  }

  const onRemoveSigner = async () => {
    if (deleteSignerMutation.isLoading) return;
    const res = await deleteSignerMutation.mutateAsync(signer.eSignParticipantId);
    if (!res.data.hasErrors) {
      setSigner(null);
    } else {
      window.displayGenericModal("Error", res.data.errors[0].message, "tiny", "red");
    }
  }

  const documentType = viewModel.details.documentType as DocumentTypeWithoutUnknown;

  const { hasErrors } = isDraftValid();

  const modals = useModals();

  const onValueChanged = (key: string, value: any) => {
    if (key === "bookkeeping") {
      const v = value as server.dto.BookkeepingSmall;
      setEsignDocumentFormState({
        ...esignDocumentFormState,
        bookkeeping: v,
        currency: v.baseCurrency,
        exchangeRate: 1,
      });
      onBookkeepingOrCurrencyChange(v);
    } else if (key === "currencyExchangeRate") {
      setEsignDocumentFormState({
        ...esignDocumentFormState,
        currency: value.currency,
        exchangeRate: value.exchangeRate,
      });
      onBookkeepingOrCurrencyChange(esignDocumentFormState.bookkeeping);
    } else if (key === "brand") {
      setEsignDocumentFormState({ ...esignDocumentFormState, brand: value });
    } else if (key === "dueInDays") {
      setEsignDocumentFormState({ ...esignDocumentFormState, dueInDays: value });
    } else if (key === "owner") {
      setEsignDocumentFormState({ ...esignDocumentFormState, owner: value });
    } else if (key === "allowAutoSign") {
      setEsignDocumentFormState({ ...esignDocumentFormState, allowAutoSign: value });
    } else if (key === "sender_user") {
      setSender(prev => ({ ...prev, linkedToUser: value }));
    } else if (key === "sender_contact") {
      if (value == null) {
        setSender(null);
        return;
      }
      if (value.contactId) {
        setSender(prev => ({
          ...prev,
          contact: value
        }));
        return;
      }
      const val = value as ContactParticipant;
      setSender(prev => ({
        ...prev,
        contact: {
          contactId: val.contact.contactId,
          name: val.contact.name,
          gender: "unspecified",
          position: val.contact.position,
          culture: val.contact.culture,
          isValidated: true,
        },
        email: val.email,
        culture: val.culture,
        company: val.company
      }));
      setNoSenderError(false);
    } else if (key === "sender_representing") {
      setSender(prev => ({
        ...prev,
        company: value
      }));
    } else if (key === "sender_email") {
      setSender(prev => ({
        ...prev,
        email: value
      }));
    } else if (key === "sender_language") {
      setSender(prev => ({
        ...prev,
        culture: serverReference.templateCultures.find(c => c.lcid === value)
      }));
    } else if (key === "signer_contact") {
      if (value == null) {
        setSigner(null);
        return;
      }
      if (value.contactId) {
        setSender(prev => ({
          ...prev,
          contact: value
        }));
        return;
      }
      const val = value as ContactParticipant;
      setSigner(prev => ({
        ...prev,
        contact: {
          contactId: val.contact.contactId,
          name: val.contact.name,
          gender: "unspecified",
          position: val.contact.position,
          culture: val.contact.culture,
          isValidated: true,
        },
        email: val.email,
        company: val.company,
        invitationSubject: val.invitationSubject,
        invitationBody: val.invitationBody,
        paths: {
          acceptAbsolute: null,
          appleSignIn: null,
          googleSignIn: null,
          landingAbsolute: null,
          provideAbsolute: null,
        },
      }));
      setNoSignerError(false);
    } else if (key === "signer_representing") {
      setSigner(prev => ({
        ...prev,
        company: value
      }));
    } else if (key === "signer_email") {
      setSigner(prev => ({
        ...prev,
        email: value
      }));
    } else if (key === "signer_language") {
      setSigner(prev => ({
        ...prev,
        culture: serverReference.templateCultures.find(c => c.lcid === value)
      }));
    } else if (key === "signer_templateSubject") {
      setSigner(prev => ({
        ...prev,
        invitationSubject: value,
      }));
    } else if (key === "signer_templateBody") {
      setSigner(prev => ({
        ...prev,
        invitationBody: value
      }));
    } else {
      console.warn("Unhandled key in onValueChanged:", key);
      console.warn("Value:", value);
    }
  }

  const onSuccessfulUpdate = async (key: string) => {
    if (key === "signer_contact") {
      const updatedSignerRes = await window.CoreApi.ESign.ListParticipants({
        documentId: getDocumentId(),
        role: "signer",
        page: 1,
        perPage: 2
      });
      const signer = updatedSignerRes.data.data[0];
      setSigner(signer);
    } else if (key === "signer_representing") {
      modals.displayFinancialRiskModal(signer.company.companyId);
    } else if (key === "currencyExchangeRate" || key === "bookkeeping") {
      queryClient.invalidateQueries(productsByParentQueryKey);
    } else if (key.endsWith("sender_language")) {
      invalidateContactCache(queryClient, sender.contact.contactId);
    } else if (key.endsWith("signer_language")) {
      invalidateContactCache(queryClient, signer.contact.contactId);
    }
  }


  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <DetailSectionTemplate
        isInPageEditable
        onValueChanged={onValueChanged}
        onSuccessfulUpdate={onSuccessfulUpdate}
        sections={[
          EsignDraftSection({
            documentType,
            document: viewModel.details,
            brands: viewModel.brands,
            hasErrors,
            formState: esignDocumentFormState,
            onReviewClickValidate
          }),
          SenderPanel({
            esignDocument: viewModel.details,
            sender,
            progress: viewModel.senderProgress,
            draft: {
              bookkeeping,
              onRemoveSender,
              onSenderChange: (sender) => {
                setSender(sender);
                setNoSenderError(false);
              },
              isRemovingSender: deleteSenderMutation.isLoading,
              noSenderError
            }
          }),
          SignerPanel({
            esignDocument: viewModel.details,
            signer,
            progress: viewModel.signerProgress,
            draft: {
              onRemoveSigner,
              onSignerChange: (signer) => {
                setSigner(signer);
                setNoSignerError(false);
                modals.displayFinancialRiskModal(signer.company.companyId);
              },
              isRemovingSigner: deleteSignerMutation.isLoading,
              noSignerError
            }
          }),
          {
            type: "custom",
            key: "viewPanel",
            render: () => <DraftViewerPanel
              initialViewers={viewModel.viewers}
              document={viewModel.details}
            />
          }, {
            type: "custom",
            key: "linkedShowsPanel",
            render: () => <LinkedShowsPanel
              documentType={viewModel.details.documentType}
              config={viewModel.linkedShowsPanel.config}
              initialShowCount={viewModel.linkedShowsPanel.data.collection.length}
              queryKey={["linked-shows"]}
              onShowsChanged={onLinkedShowsSaved}
              defaultBookkeeping={bookkeeping}
            />
          }, {
            type: "custom",
            key: "productsPanel",
            render: () => <ProductsPanel
              useSectionPanel
              productsPanel={viewModel.productsPanel}
              documentType={viewModel.details.documentType}
              documentId={getDocumentId()}
            />
          }, {
            type: "custom",
            key: "filesThatNeedSigningPanel",
            render: () => <FilesPanel
              divId="filesThatNeedSigningPanel"
              label={RESX.ESign.PdfsThatNeedToBeSigned}
              fileGroup="needToBeSigned"
              files={filesNeedingSigning}
              onFilesUpdated={files => {
                setFilesNeedingSigning(files.filter(f => f.fileGroup === "needToBeSigned"));
                setFileError(false);
              }}
              error={fileError}
              documentDetails={viewModel.details}
              startUncollapsed
              collapsible={true}
              articleId={serverReference.supportArticleIds.pdfsThatNeedToBeSigned}
              validateFromTemplateClick={validateOnFromTemplateClick}
              bookkeeping={bookkeeping}
            />
          }, {
            type: "custom",
            key: "viewDocumentButton",
            hidden: filesNeedingSigning.length === 0,
            render: () => <ViewDocumentButton
              label={RESX.ESign.ViewDocument}
              documentNumber={viewModel.details.documentNumber}
              version={viewModel.details.version}
              documentId={viewModel.details.documentId}
            />
          }, {
            key: "otherFilesPanel",
            type: "custom",
            render: () => <FilesPanel
              divId="otherFilesPanel"
              label={RESX.ESign.OtherFiles}
              fileGroup="otherFiles"
              files={otherFiles}
              onFilesUpdated={files => {
                setOtherFiles(files.filter(f => f.fileGroup === "otherFiles"));
                setFileError(false);
              }}
              error={false}
              documentDetails={viewModel.details}
              startUncollapsed
              collapsible={false}
              articleId={serverReference.supportArticleIds.eSignOtherFiles}
              bookkeeping={bookkeeping}
            />
          }, {
            key: "notesPanel",
            type: "custom",
            render: () => <NotesPanel
              mainArea={documentType}
              useSectionPanel
              config={viewModel.linkedNotePanelConfig}
            />
          }, {
            type: "custom",
            key: "changeLifecycleButtons",
            render: () => <SectionPanel.Panel>
              <ChangeLifecycleButtons
                documentId={getDocumentId()}
                right="review"
                rightValidate={onReviewClickValidate}
                documentType={documentType}
              />
            </SectionPanel.Panel>
          }
        ]}
      />
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn size="medium">
      <ESignOptionsPanel
        optionsPanel={viewModel.optionsPanel}
        listUrl={viewModel.listUrl}
        documentDetails={viewModel.details}
        copyUrl={viewModel.copyUrl}
      />

      <DocumentReminderApp
        document={viewModel.details}
        initialReminders={viewModel.reminders}
      />

      <ESignActivityPanel
        documentId={getDocumentId()}
        config={viewModel.activityPanel}
      />
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>
};

export default DraftPanel;